import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SearchableDropdown } from "./SearchableDropdown";
import { SearchableDropdownComponent } from "../../services/types/ComponentTypes";
import { apiService } from "../../services/api";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { fetchLookup } from "../../state/slices/lookupsSlice";
import { showNotification } from "../../state/slices/notificationsSlice";

const LEDGER_LOOKUP = "get_all_ledgers";

export type LedgerSettingsValues = {
  receipt_voucher_debit: boolean;
  payment_voucher_debit: boolean;
  journal_vouchers_ledger: boolean;
  contra_voucher_credit: boolean;
  contra_voucher_debit: boolean;
  petty_cash_credit: boolean;
  petty_cash_debit: boolean;
  cash_payments_credit: boolean;
  cash_payments_debit: boolean;
  bank_payment_credit: boolean;
  bank_payment_debit: boolean;
  credit_note_ledger: boolean;
  debit_note_ledger: boolean;
};

const SETTING_KEYS = [
  "receipt_voucher_debit",
  "payment_voucher_debit",
  "journal_vouchers_ledger",
  "contra_voucher_credit",
  "contra_voucher_debit",
  "petty_cash_credit",
  "petty_cash_debit",
  "cash_payments_credit",
  "cash_payments_debit",
  "bank_payment_credit",
  "bank_payment_debit",
  "credit_note_ledger",
  "debit_note_ledger",
] as const satisfies readonly (keyof LedgerSettingsValues)[];

type SettingItem = {
  key: keyof LedgerSettingsValues;
  label: string;
  hint: string;
};

const SETTING_ITEMS: SettingItem[] = [
  { key: "receipt_voucher_debit", label: "Receipt voucher debit", hint: "Receipt voucher — debit side" },
  { key: "payment_voucher_debit", label: "Payment voucher debit", hint: "Payment voucher — debit side" },
  { key: "journal_vouchers_ledger", label: "Journal vouchers ledger", hint: "Journal vouchers — ledger" },
  { key: "contra_voucher_credit", label: "Contra voucher credit", hint: "Contra voucher — credit" },
  { key: "contra_voucher_debit", label: "Contra voucher debit", hint: "Contra voucher — debit" },
  { key: "petty_cash_credit", label: "Petty cash credit", hint: "Petty cash — credit" },
  { key: "petty_cash_debit", label: "Petty cash debit", hint: "Petty cash — debit" },
  { key: "cash_payments_credit", label: "Cash payments credit", hint: "Cash payments — credit" },
  { key: "cash_payments_debit", label: "Cash payments debit", hint: "Cash payments — debit" },
  { key: "bank_payment_credit", label: "Bank payment credit", hint: "Bank payment — credit" },
  { key: "bank_payment_debit", label: "Bank payment debit", hint: "Bank payment — debit" },
  // { key: "credit_note_ledger", label: "Credit note ledger", hint: "Credit note — ledger" },
  // { key: "debit_note_ledger", label: "Debit note ledger", hint: "Debit note — ledger" },
];

function emptySettings(): LedgerSettingsValues {
  return SETTING_KEYS.reduce(
    (acc, key) => {
      acc[key] = false;
      return acc;
    },
    {} as LedgerSettingsValues
  );
}

/** null / missing / no row → false for every field */
function coerceBoolean(value: unknown): boolean {
  if (value === true || value === 1 || value === "1" || value === "true" || value === "True") {
    return true;
  }
  return false;
}

function mapApiToSettings(data: Record<string, unknown> | null | undefined): LedgerSettingsValues {
  const next = emptySettings();
  if (!data) return next;

  SETTING_KEYS.forEach((key) => {
    next[key] = coerceBoolean(data[key]);
  });
  return next;
}

type SettingToggleCardProps = {
  item: SettingItem;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
};

const SettingToggleCard: React.FC<SettingToggleCardProps> = ({
  item,
  checked,
  disabled,
  onToggle,
}) => {
  const inputId = `ledger-setting-${item.key}`;

  return (
    <div
      className={[
        "relative flex items-center justify-between gap-3 rounded-xl border p-3.5 min-h-[88px] transition-all duration-200 w-full",
        checked
          ? "border-[var(--primary-green)]/40 bg-emerald-50/80 dark:bg-emerald-950/25 shadow-sm"
          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:border-gray-300 dark:hover:border-gray-600",
        disabled ? "opacity-60 pointer-events-none" : "",
      ].join(" ")}
    >
      <div className="min-w-0 flex-1 pr-2">
        <label
          htmlFor={inputId}
          className="block text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer leading-snug"
        >
          {item.label}
        </label>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          {""}
        </p>
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        id={inputId}
        disabled={disabled}
        onClick={onToggle}
        className={[
          "relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-green)] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900",
          checked ? "bg-[var(--primary-green)]" : "bg-gray-300 dark:bg-gray-600",
        ].join(" ")}
      >
        <span
          className={[
            "inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200",
            checked ? "translate-x-6" : "translate-x-1",
          ].join(" ")}
        />
      </button>
    </div>
  );
};

export type LedgerSettingsMasterProps = {
  config?: Record<string, unknown>;
};

export const LedgerSettingsMaster: React.FC<LedgerSettingsMasterProps> = () => {
  const dispatch = useAppDispatch();
  const lookupOptions = useAppSelector((state) => state.lookups.lookups[LEDGER_LOOKUP] || []);
  const lookupLoading = useAppSelector((state) => state.lookups.loading[LEDGER_LOOKUP]);

  const [ledgerId, setLedgerId] = useState<string | number | "">("");
  const [settings, setSettings] = useState<LedgerSettingsValues>(emptySettings);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [hasSavedRecord, setHasSavedRecord] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const ledgerDropdownConfig = useMemo<SearchableDropdownComponent>(
    () => ({
      componentType: "searchableDropdown" as any,
      name: "drp_ledger_id",
      label: "Ledger",
      placeholder: "Search and select ledger…",
      required: true,
      lookupName: LEDGER_LOOKUP,
      options: lookupOptions,
      IsVerticalView: true,
    }),
    [lookupOptions]
  );

  useEffect(() => {
    dispatch(fetchLookup({ lookupName: LEDGER_LOOKUP }));
  }, [dispatch]);

  const loadSettings = useCallback(
    async (parentId: string | number) => {
      setLoadingSettings(true);
      setLoadError(null);
      setSettingsId(null);
      setHasSavedRecord(false);

      try {
        const res = await apiService.get<{
          success?: boolean;
          data?: Record<string, unknown> | null;
          message?: string;
        }>(
          `/ledger-settings/getSettingsByparentid/${encodeURIComponent(String(parentId))}`,
          undefined,
          { includeAuthToken: true, errorHandling: "throw" }
        );

        const row = res?.data ?? null;
        const exists = row != null && typeof row === "object";

        setSettings(mapApiToSettings(row));
        setHasSavedRecord(exists);
        setSettingsId(exists && row.id != null ? String(row.id) : null);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load ledger settings";
        setLoadError(msg);
        setSettings(emptySettings());
        setHasSavedRecord(false);
        dispatch(showNotification({ message: msg, type: "error" }));
      } finally {
        setLoadingSettings(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (ledgerId === "" || ledgerId == null) {
      setSettings(emptySettings());
      setSettingsId(null);
      setHasSavedRecord(false);
      setLoadError(null);
      return;
    }
    loadSettings(ledgerId);
  }, [ledgerId, loadSettings]);

  const handleLedgerChange = (
    _name: string,
    value: string | number | boolean | (string | number | boolean)[]
  ) => {
    if (Array.isArray(value) || typeof value === "boolean") return;
    setLedgerId(value === "" || value == null ? "" : value);
  };

  const handleToggle = (key: keyof LedgerSettingsValues) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (ledgerId === "" || ledgerId == null) {
      dispatch(showNotification({ message: "Please select a ledger first.", type: "error" }));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        parent_id: String(ledgerId),
        ...settings,
      };

      const res = await apiService.post<{
        success?: boolean;
        action?: "created" | "updated";
        message?: string;
        id?: string;
        data?: Record<string, unknown>;
      }>("/api/ledger-settings/saveLedgerSettings", payload, {
        includeAuthToken: true,
        errorHandling: "throw",
      });

      if (res?.data) {
        setSettings(mapApiToSettings(res.data));
        setHasSavedRecord(true);
        if (res.id) setSettingsId(String(res.id));
        else if (res.data.id != null) setSettingsId(String(res.data.id));
      }

      const actionLabel = res?.action === "updated" ? "updated" : "saved";
      dispatch(
        showNotification({
          message: res?.message || `Ledger settings ${actionLabel} successfully.`,
          type: "success",
        })
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to save ledger settings";
      dispatch(showNotification({ message: msg, type: "error" }));
    } finally {
      setSaving(false);
    }
  };

  const hasLedger = ledgerId !== "" && ledgerId != null;
  const selectedLabel = lookupOptions.find((o) => String(o.value) === String(ledgerId))?.label;
  const enabledCount = SETTING_KEYS.filter((k) => settings[k]).length;

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-5 shadow-sm">
        <div className="mb-1">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Select ledger
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Choose a ledger to configure voucher posting rules.
          </p>
        </div>
        <div className="mt-4">
          <SearchableDropdown
            config={ledgerDropdownConfig}
            value={ledgerId}
            onChange={handleLedgerChange}
          />
          {lookupLoading && !lookupOptions.length && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Loading ledgers…</p>
          )}
        </div>
      </div>

      {!hasLedger && (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900/30 px-6 py-10 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Select a ledger above to view and edit individual voucher settings.
          </p>
        </div>
      )}

      {hasLedger && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {selectedLabel ? (
                  <>
                    Configuring{" "}
                    <span className="text-[var(--primary-green)] dark:text-emerald-400">
                      {selectedLabel}
                    </span>
                  </>
                ) : (
                  "Ledger settings"
                )}
              </p>
              {!loadingSettings && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {hasSavedRecord
                    ? `${enabledCount} of ${SETTING_ITEMS.length} enabled`
                    : "No saved settings — all options start disabled until you turn them on"}
                </p>
              )}
            </div>
            {/* {settingsId && (
              <span className="text-xs font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                ID {settingsId}
              </span>
            )} */}
          </div>

          {loadingSettings ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-gray-500 dark:text-gray-400">
              <span className="w-8 h-8 border-2 border-[var(--primary-green)] border-t-transparent rounded-full animate-spin" />
              Loading settings…
            </div>
          ) : (
            <>
              {loadError && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                  Could not load saved settings. All options are off — adjust and save to create a
                  record.
                </div>
              )}

              {/* {!loadError && !hasSavedRecord && (
                <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50/80 dark:bg-blue-950/20 px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
                  First time for this ledger: every setting is off. Enable what you need, then save.
                </div>
              )} */}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
                {SETTING_ITEMS.map((item) => (
                  <SettingToggleCard
                    key={item.key}
                    item={item}
                    checked={settings[item.key]}
                    disabled={saving}
                    onToggle={() => handleToggle(item.key)}
                  />
                ))}
              </div>

              <div className="sticky bottom-0 z-10 -mx-1 px-1 pt-2 pb-1 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md px-4 py-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Changes apply only after you save.
                  </span>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || loadingSettings}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-[var(--primary-green)] hover:brightness-95 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {saving ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Saving…
                      </>
                    ) : (
                      "Save settings"
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LedgerSettingsMaster;
